import React, {
  useState,
  memo,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CloudArrowUpIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  PhotoIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import api from "../../services/api";

interface Photo {
  id: string;
  url: string;
  originalName: string;
  size: number;
  width: number;
  height: number;
  isActive: boolean;
  uploadedBy: string;
  uploadedAt: string;
}

// Memoized PhotoCard component to prevent unnecessary re-renders
const PhotoCard = memo(
  ({
    photo,
    onToggle,
    onDelete,
    onRetryDelete,
    isToggling,
    isDeleting,
    isFailedDeletion,
  }: {
    photo: Photo;
    onToggle: (id: string) => void;
    onDelete: (id: string) => void;
    onRetryDelete: (id: string) => void;
    isToggling: boolean;
    isDeleting: boolean;
    isFailedDeletion: boolean;
  }) => {
    const formatFileSize = useCallback((bytes: number) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }, []);

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="aspect-w-16 aspect-h-12 bg-gray-100">
          <img
            src={photo.url}
            alt={photo.originalName}
            className="w-full h-48 object-cover"
          />
        </div>

        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {photo.originalName}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {photo.width} × {photo.height} • {formatFileSize(photo.size)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {new Date(photo.uploadedAt).toLocaleDateString()}
          </p>

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => onToggle(photo.id)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  photo.isActive
                    ? "text-green-600 hover:bg-green-50 hover:text-green-700"
                    : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                } cursor-pointer`}
                title={photo.isActive ? "Hide from gallery" : "Show in gallery"}
              >
                <motion.div
                  whileTap={{ scale: 0.8 }}
                  transition={{ duration: 0.1 }}
                >
                  {photo.isActive ? (
                    <EyeIcon className="h-5 w-5" />
                  ) : (
                    <EyeSlashIcon className="h-5 w-5" />
                  )}
                </motion.div>
              </motion.button>

              {isFailedDeletion ? (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onRetryDelete(photo.id)}
                  className="p-2 rounded-lg transition-all duration-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 cursor-pointer"
                  title="Retry deletion"
                >
                  <motion.div
                    whileTap={{ scale: 0.8 }}
                    transition={{ duration: 0.1 }}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </motion.div>
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to delete this photo?"
                      )
                    ) {
                      onDelete(photo.id);
                    }
                  }}
                  className="p-2 rounded-lg transition-all duration-200 text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer"
                  title="Delete photo"
                >
                  <motion.div
                    whileTap={{ scale: 0.8 }}
                    transition={{ duration: 0.1 }}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </motion.div>
                </motion.button>
              )}
            </div>

            <div
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                photo.isActive
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-gray-100 text-gray-600 border border-gray-200"
              }`}
            >
              {photo.isActive ? "Visible" : "Hidden"}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
);

PhotoCard.displayName = "PhotoCard";

// Custom comparison function for PhotoCard to prevent unnecessary re-renders
const areEqual = (prevProps: any, nextProps: any) => {
  return (
    prevProps.photo.id === nextProps.photo.id &&
    prevProps.photo.isActive === nextProps.photo.isActive &&
    prevProps.photo.url === nextProps.photo.url &&
    prevProps.photo.originalName === nextProps.photo.originalName &&
    prevProps.isToggling === nextProps.isToggling &&
    prevProps.isDeleting === nextProps.isDeleting &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.onDelete === nextProps.onDelete
  );
};

// Apply the custom comparison to PhotoCard
const OptimizedPhotoCard = memo(PhotoCard, areEqual);

const AdminPhotosPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [uploadTimeout, setUploadTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [failedDeletions, setFailedDeletions] = useState<Set<string>>(
    new Set()
  );
  const queryClient = useQueryClient();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (uploadTimeout) {
        clearTimeout(uploadTimeout);
      }
    };
  }, [uploadTimeout]);

  // Fetch photos with pagination
  const { data: photosData, isLoading } = useQuery({
    queryKey: ["admin-photos", currentPage],
    queryFn: async () => {
      const response = await api.get(
        `/photos/admin?page=${currentPage}&limit=20`
      );
      return response.data;
    },
  });

  // Single photo upload mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("photo", file);

      const response = await api.post("/photos/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000, // 5 minutes timeout for single upload
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (uploadTimeout) {
        clearTimeout(uploadTimeout);
        setUploadTimeout(null);
      }
      queryClient.invalidateQueries({
        queryKey: ["admin-photos", currentPage],
      });
      setSelectedFiles([]);
      setIsUploading(false);
      setSuccessMessage(
        `Photo "${data.data.originalName}" uploaded successfully!`
      );
      setErrorMessage(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      if (uploadTimeout) {
        clearTimeout(uploadTimeout);
        setUploadTimeout(null);
      }
      console.error("Upload error:", error);
      setIsUploading(false);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to upload photo. Please try again."
      );
      setSuccessMessage(null);
      setTimeout(() => setErrorMessage(null), 5000);
    },
  });

  // Bulk upload mutation
  const bulkUploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("photos", file);
      });

      const response = await api.post("/photos/bulk-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 600000, // 10 minutes timeout for bulk upload
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress((prev) => ({
            ...prev,
            bulk: percentCompleted,
          }));
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (uploadTimeout) {
        clearTimeout(uploadTimeout);
        setUploadTimeout(null);
      }
      console.log("Bulk upload success:", data);
      queryClient.invalidateQueries({
        queryKey: ["admin-photos", currentPage],
      });
      setSelectedFiles([]);
      setIsUploading(false);
      setUploadProgress({});

      if (data.data.successful > 0) {
        setSuccessMessage(
          `Successfully uploaded ${data.data.successful} photos! ${
            data.data.failed > 0 ? `${data.data.failed} failed.` : ""
          }`
        );
        setErrorMessage(null);
      } else {
        setErrorMessage("No photos were uploaded successfully.");
        setSuccessMessage(null);
      }
      setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 5000);
    },
    onError: (error: any) => {
      if (uploadTimeout) {
        clearTimeout(uploadTimeout);
        setUploadTimeout(null);
      }
      console.error("Bulk upload error:", error);
      console.error("Error details:", {
        message: error?.response?.data?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setIsUploading(false);
      setUploadProgress({});

      // Check if it's a partial success (some files uploaded)
      if (error?.response?.data?.data?.successful > 0) {
        setSuccessMessage(
          `Partially successful: ${error.response.data.data.successful} uploaded, ${error.response.data.data.failed} failed.`
        );
        setErrorMessage(null);
        // Still refresh the photos list
        queryClient.invalidateQueries({
          queryKey: ["admin-photos", currentPage],
        });
      } else {
        // Only show error if it's a real network/server error (not a timeout or partial success)
        const isNetworkError =
          !error?.response ||
          error?.code === "NETWORK_ERROR" ||
          error?.code === "TIMEOUT";
        const isServerError = error?.response?.status >= 500;

        if (isNetworkError || isServerError) {
          // Check if it's a timeout error specifically
          if (
            error?.code === "TIMEOUT" ||
            error?.message?.includes("timeout")
          ) {
            setErrorMessage(
              "Upload timeout - your connection may be slow or files too large. Please try uploading fewer files at once or check your internet connection."
            );
          } else {
            setErrorMessage(
              error?.response?.data?.message ||
                "Network error. Please check your connection and try again."
            );
          }
          setSuccessMessage(null);
        } else {
          // For other errors, show a more generic message and still refresh the list
          setSuccessMessage(
            "Upload completed. Please check the gallery for your photos."
          );
          setErrorMessage(null);
          queryClient.invalidateQueries({
            queryKey: ["admin-photos", currentPage],
          });
        }
      }

      setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 5000);
    },
  });

  // Toggle photo visibility mutation
  const togglePhotoMutation = useMutation({
    mutationFn: async (photoId: string) => {
      const response = await api.patch(`/photos/${photoId}/toggle`);
      return response.data;
    },
    onMutate: async (photoId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["admin-photos", currentPage],
      });

      // Snapshot the previous value
      const previousPhotos = queryClient.getQueryData([
        "admin-photos",
        currentPage,
      ]);

      // Optimistically update only the specific photo
      queryClient.setQueryData(["admin-photos", currentPage], (old: any) => {
        if (!old?.data?.photos) return old;

        return {
          ...old,
          data: {
            ...old.data,
            photos: old.data.photos.map((photo: Photo) =>
              photo.id === photoId
                ? { ...photo, isActive: !photo.isActive }
                : photo
            ),
          },
        };
      });

      return { previousPhotos, photoId };
    },
    onSuccess: (data, photoId) => {
      // Update only the specific photo with server response
      queryClient.setQueryData(["admin-photos", currentPage], (old: any) => {
        if (!old?.data?.photos) return old;

        return {
          ...old,
          data: {
            ...old.data,
            photos: old.data.photos.map((photo: Photo) =>
              photo.id === photoId
                ? { ...photo, isActive: data.data.photo.isActive }
                : photo
            ),
          },
        };
      });
      setSuccessMessage(
        `Photo ${data.data.photo.isActive ? "shown" : "hidden"} successfully!`
      );
      setErrorMessage(null);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any, photoId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousPhotos) {
        queryClient.setQueryData(
          ["admin-photos", currentPage],
          context.previousPhotos
        );
      }
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to update photo visibility. Please try again."
      );
      setSuccessMessage(null);
      // Clear error message after 5 seconds
      setTimeout(() => setErrorMessage(null), 5000);
    },
  });

  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: async (photoId: string) => {
      const response = await api.delete(`/photos/${photoId}`);
      return response.data;
    },
    onMutate: async (photoId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["admin-photos", currentPage],
      });

      // Snapshot the previous value
      const previousPhotos = queryClient.getQueryData([
        "admin-photos",
        currentPage,
      ]);

      // Optimistically remove the photo
      queryClient.setQueryData(["admin-photos", currentPage], (old: any) => {
        if (!old?.data?.photos) return old;

        return {
          ...old,
          data: {
            ...old.data,
            photos: old.data.photos.filter(
              (photo: Photo) => photo.id !== photoId
            ),
            pagination: {
              ...old.data.pagination,
              total: old.data.pagination.total - 1,
            },
          },
        };
      });

      return { previousPhotos, photoId };
    },
    onSuccess: (data, photoId) => {
      // Photo successfully deleted, remove from failed deletions if it was there
      setFailedDeletions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
      setSuccessMessage("Photo deleted successfully!");
      setErrorMessage(null);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any, photoId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousPhotos) {
        queryClient.setQueryData(
          ["admin-photos", currentPage],
          context.previousPhotos
        );
      }

      // Check for specific Cloudinary timeout errors
      const isCloudinaryTimeout =
        error?.response?.data?.error?.name === "TimeoutError" ||
        error?.response?.data?.error?.http_code === 499;

      if (isCloudinaryTimeout) {
        // Add to failed deletions for retry
        setFailedDeletions((prev) => new Set(prev).add(photoId));
        setErrorMessage(
          "Photo deletion timed out. The photo may still be deleted from the gallery. Please refresh the page to check."
        );
        // For timeout errors, also refresh the photos list to check if deletion actually succeeded
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ["admin-photos", currentPage],
          });
        }, 2000);
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "Failed to delete photo. Please try again."
        );
      }
      setSuccessMessage(null);
      // Clear error message after 8 seconds for timeout errors
      setTimeout(() => setErrorMessage(null), 8000);
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    console.log("Files selected:", files.length);

    if (files.length > 0) {
      // Validate files
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      files.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          invalidFiles.push(`${file.name} - Not an image file`);
        } else if (file.size > 10 * 1024 * 1024) {
          invalidFiles.push(`${file.name} - File size must be less than 10MB`);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        alert(`Invalid files:\n${invalidFiles.join("\n")}`);
      }

      if (validFiles.length > 0) {
        console.log("Setting valid files:", validFiles.length);
        setSelectedFiles(validFiles);
      }
    }

    // Reset the input to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    console.log("Upload clicked, selected files:", selectedFiles.length);
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    // Set a longer timeout to handle slow uploads and large files
    const timeout = setTimeout(() => {
      if (isUploading) {
        setSuccessMessage(
          "Upload is taking longer than expected. Please check the gallery for your photos."
        );
        setIsUploading(false);
        setUploadProgress({});
        queryClient.invalidateQueries({
          queryKey: ["admin-photos", currentPage],
        });
      }
    }, 120000); // 2 minutes timeout for slow connections

    setUploadTimeout(timeout);

    if (selectedFiles.length === 1) {
      uploadPhotoMutation.mutate(selectedFiles[0]);
    } else {
      bulkUploadMutation.mutate(selectedFiles);
    }
  };

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  const handleToggle = useCallback(
    (photoId: string) => {
      togglePhotoMutation.mutate(photoId);
    },
    [togglePhotoMutation]
  );

  const handleDelete = useCallback(
    (photoId: string) => {
      deletePhotoMutation.mutate(photoId);
    },
    [deletePhotoMutation]
  );

  const handleRetryDelete = useCallback(
    (photoId: string) => {
      deletePhotoMutation.mutate(photoId);
    },
    [deletePhotoMutation]
  );

  // Memoize the photos list to prevent unnecessary re-renders
  const photos = useMemo(
    () => photosData?.data?.photos || [],
    [photosData?.data?.photos]
  );

  // Pagination data
  const pagination = photosData?.data?.pagination;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 sm:mb-8"
        >
          {/* Mobile-first header layout */}
          <div className="space-y-4 sm:space-y-0">
            {/* Title and icon - full width on mobile */}
            <div className="flex items-center space-x-3">
              <PhotoIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Event Photos
              </h1>
            </div>

            {/* Photo count - full width on mobile, right-aligned on desktop */}
            <div className="text-sm text-gray-500 sm:text-right">
              {pagination
                ? `${pagination.total} photo${
                    pagination.total !== 1 ? "s" : ""
                  } uploaded`
                : `${photos.length} photo${
                    photos.length !== 1 ? "s" : ""
                  } uploaded`}
            </div>
          </div>

          {/* Back to Dashboard Button - full width on mobile */}
          <div className="mt-4 sm:mt-6">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </button>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center"
            >
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800">{successMessage}</span>
            </motion.div>
          )}

          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center"
            >
              <XMarkIcon className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800">{errorMessage}</span>
            </motion.div>
          )}

          {/* Upload Section */}
          <motion.div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-8 mb-6 hover:border-blue-400 hover:bg-blue-50/20 transition-all duration-300 group shadow-sm">
            <div className="text-center">
              <motion.div
                animate={{
                  scale: isUploading ? [1, 1.05, 1] : 1,
                  rotate: isUploading ? [0, 2, -2, 0] : 0,
                }}
                transition={{
                  duration: 0.6,
                  repeat: isUploading ? Infinity : 0,
                }}
                className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-6 shadow-lg"
              >
                <CloudArrowUpIcon className="h-8 w-8 text-blue-600" />
              </motion.div>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                Upload Event Photos
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Drag and drop photos here or click to select multiple files
              </p>

              <input
                ref={fileInputRef}
                id="photo-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="photo-upload"
                    className="cursor-pointer inline-flex items-center px-4 sm:px-6 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/30 transition-all duration-200 group-hover:border-blue-400 group-hover:text-blue-600 group-hover:bg-blue-50/30 w-full sm:w-auto justify-center"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    <span className="font-medium text-sm sm:text-base">
                      Choose Photos
                    </span>
                  </label>
                </div>

                {selectedFiles.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200"
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {selectedFiles.length} Photo
                          {selectedFiles.length > 1 ? "s" : ""} Selected
                        </h4>
                        <button
                          onClick={() => setSelectedFiles([])}
                          className="text-sm text-gray-500 hover:text-red-600 transition-colors duration-200 font-medium"
                        >
                          Clear All
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-3 max-h-32 sm:max-h-40 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center justify-between bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-200"
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="h-8 w-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                                <PhotoIcon className="h-4 w-4 text-gray-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(file.size)}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {selectedFiles.length > 0 && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="w-full inline-flex items-center justify-center px-4 sm:px-6 py-3 border border-transparent text-sm sm:text-base font-semibold rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                        <div className="flex flex-col items-center">
                          <span className="text-sm">
                            Uploading {selectedFiles.length} Photo
                            {selectedFiles.length > 1 ? "s" : ""}...
                          </span>
                          {uploadProgress.bulk > 0 && (
                            <span className="text-xs opacity-75 mt-1">
                              {uploadProgress.bulk}% complete
                              {uploadProgress.bulk < 20 &&
                                " - Please be patient with slow connections"}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="h-5 w-5 mr-3" />
                        <span>
                          Upload {selectedFiles.length} Photo
                          {selectedFiles.length > 1 ? "s" : ""}
                        </span>
                      </>
                    )}
                  </motion.button>
                )}

                {uploadProgress.bulk > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-3"
                  >
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Upload Progress</span>
                      <span>{uploadProgress.bulk}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <motion.div
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress.bulk}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">
                        {uploadProgress.bulk < 100
                          ? uploadProgress.bulk < 50
                            ? "Uploading photos... This may take a while on slow connections"
                            : "Processing your photos, please wait..."
                          : "Finalizing upload..."}
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Photos Grid */}
          {photos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {photos.map((photo: Photo) => (
                  <OptimizedPhotoCard
                    key={photo.id}
                    photo={photo}
                    onToggle={handleToggle}
                    onDelete={handleDelete}
                    onRetryDelete={handleRetryDelete}
                    isToggling={false}
                    isDeleting={false}
                    isFailedDeletion={failedDeletions.has(photo.id)}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {pagination && pagination.pages > 1 && (
                <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-0">
                  <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                    Showing {(currentPage - 1) * pagination.limit + 1} to{" "}
                    {Math.min(currentPage * pagination.limit, pagination.total)}{" "}
                    of {pagination.total} photos
                  </div>

                  <div className="flex items-center justify-center sm:justify-end space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={!pagination.hasPrev}
                      className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    <div className="flex space-x-1">
                      {Array.from(
                        { length: Math.min(5, pagination.pages) },
                        (_, i) => {
                          const pageNum =
                            Math.max(
                              1,
                              Math.min(pagination.pages - 4, currentPage - 2)
                            ) + i;
                          if (pageNum > pagination.pages) return null;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 text-sm font-medium rounded-md ${
                                currentPage === pageNum
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(pagination.pages, prev + 1)
                        )
                      }
                      disabled={!pagination.hasNext}
                      className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No photos uploaded
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Upload some event photos to get started.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPhotosPage;
